(() => {
  const widgetSelector = '[data-eun-cawa]';
  const formSelector = 'form[data-type="add-to-cart-form"]';

  const getSettings = () => window.MinimogSettings || {};
  const getRoot = () => window.Shopify?.routes?.root || '/';

  const positiveInteger = (value, fallback) => {
    const parsed = Number.parseInt(String(value || ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  function isQuantityReward(widget) {
    return widget.dataset.giftMode === 'quantity_reward';
  }

  function isGiftQuantity(widget) {
    return widget.dataset.giftMode === 'gift_quantity';
  }

  function getQuantityRewardConfig(widget) {
    return {
      buyQuantity: positiveInteger(widget.dataset.buyQuantity, 10),
      rewardQuantity: positiveInteger(widget.dataset.rewardQuantity, 1)
    };
  }

  function getQuantityAccessoryVariantId(widget) {
    const item = widget.querySelector('[data-eun-cawa-quantity-accessory-item]');
    return positiveInteger(item?.dataset.accessoryVariantId, 0);
  }

  function getFormQuantity(form) {
    if (!form) return 1;
    const formData = new FormData(form);
    return Math.max(1, positiveInteger(formData.get('quantity'), 1));
  }

  function getFormForWidget(widget) {
    return [...document.querySelectorAll(formSelector)].find(form => form.dataset.productId === widget.dataset.productId) || null;
  }

  function updateSelectedCount(widget) {
    const selected = widget.querySelectorAll('[data-eun-cawa-regular] [data-eun-cawa-variant]:checked').length;
    const output = widget.querySelector('[data-eun-cawa-selected]');
    if (!output) return;

    if (isQuantityReward(widget)) {
      const freeQuantity = positiveInteger(widget.dataset.rewardGiftQuantity, 0);
      const accessoryQuantity = positiveInteger(widget.dataset.accessoryGiftQuantity, 0);
      const parts = [];
      if (freeQuantity > 0) parts.push(`${freeQuantity} Free Item${freeQuantity === 1 ? '' : 's'}`);
      if (accessoryQuantity > 0) parts.push(`${accessoryQuantity} Free Accessor${accessoryQuantity === 1 ? 'y' : 'ies'}`);
      if (selected > 0) parts.push(`${selected} Selected`);
      output.textContent = parts.length ? parts.join(' + ') : '0 Selected';
      return;
    }

    if (isGiftQuantity(widget)) {
      const freeQuantity = positiveInteger(widget.dataset.giftQuantity, 0);
      const parts = [];
      if (freeQuantity > 0) parts.push(`${freeQuantity} Free Gift${freeQuantity === 1 ? '' : 's'}`);
      if (selected > 0) parts.push(`${selected} Selected`);
      output.textContent = parts.length ? parts.join(' + ') : '0 Selected';
      return;
    }

    const giftCount = widget.querySelectorAll('[data-eun-cawa-gift-input]:checked').length;
    output.textContent = `${selected + giftCount} Selected`;
  }

  function updateQuantityRewardState(widget, form = getFormForWidget(widget)) {
    if (!isQuantityReward(widget)) return;

    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    const paidQuantity = getFormQuantity(form);
    const completedSets = Math.floor(paidQuantity / buyQuantity);
    const freeQuantity = completedSets * rewardQuantity;
    const currentCycle = paidQuantity % buyQuantity;
    const progressValue = freeQuantity > 0 && currentCycle === 0 ? buyQuantity : currentCycle;
    const remaining = currentCycle === 0 ? buyQuantity : buyQuantity - currentCycle;
    const nextFreeQuantity = freeQuantity + rewardQuantity;

    widget.dataset.rewardGiftQuantity = String(freeQuantity);
    widget.dataset.accessoryGiftQuantity = String(getQuantityAccessoryVariantId(widget) ? paidQuantity : 0);

    const heading = widget.querySelector('[data-eun-cawa-reward-heading]');
    const message = widget.querySelector('[data-eun-cawa-reward-message]');
    const progress = widget.querySelector('[data-eun-cawa-reward-progress]');
    const progressBar = widget.querySelector('[data-eun-cawa-reward-progress-bar]');
    const progressText = widget.querySelector('[data-eun-cawa-reward-progress-text]');
    const rewardItem = widget.querySelector('[data-eun-cawa-quantity-reward-item]');
    const marker = widget.querySelector('[data-eun-cawa-reward-marker]');
    const rewardLabel = widget.querySelector('[data-eun-cawa-reward-label]');
    const accessoryMessage = widget.querySelector('[data-eun-cawa-accessory-message]');
    const accessoryLabel = widget.querySelector('[data-eun-cawa-accessory-label]');

    if (heading) {
      heading.textContent = freeQuantity > 0
        ? `${freeQuantity} Free Item${freeQuantity === 1 ? '' : 's'} Unlocked`
        : `Buy ${buyQuantity}, Get ${rewardQuantity} Free`;
    }

    if (message) {
      message.textContent = freeQuantity > 0
        ? `${freeQuantity} free item${freeQuantity === 1 ? '' : 's'} will be added automatically. Add ${remaining} more to unlock ${nextFreeQuantity}.`
        : `Add ${remaining} more to unlock your free item${rewardQuantity === 1 ? '' : 's'}.`;
    }

    if (progress) {
      progress.setAttribute('aria-valuenow', String(progressValue));
      progress.setAttribute('aria-valuemax', String(buyQuantity));
    }
    if (progressBar) progressBar.style.width = `${Math.min((progressValue / buyQuantity) * 100, 100)}%`;
    if (progressText) progressText.textContent = `${progressValue} / ${buyQuantity}`;

    rewardItem?.classList.toggle('eun-cawa__item--reward-locked', freeQuantity === 0);
    rewardItem?.classList.toggle('eun-cawa__item--reward-unlocked', freeQuantity > 0);
    if (marker) marker.textContent = freeQuantity > 0 ? '✓' : '';
    if (rewardLabel) {
      rewardLabel.textContent = freeQuantity > 0 ? (widget.dataset.giftBadge || 'FREE') : 'Locked';
      rewardLabel.classList.toggle('eun-cawa__free-label--locked', freeQuantity === 0);
    }
    if (accessoryMessage) {
      accessoryMessage.textContent = `${paidQuantity} free accessor${paidQuantity === 1 ? 'y' : 'ies'} will be included.`;
    }
    if (accessoryLabel) {
      accessoryLabel.textContent = `${widget.dataset.giftBadge || 'FREE'} × ${paidQuantity}`;
    }

    updateSelectedCount(widget);
  }

  function updateGiftQuantityState(widget, form = getFormForWidget(widget)) {
    if (!isGiftQuantity(widget)) return;

    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    const paidQuantity = getFormQuantity(form);
    const giftQuantity = Math.floor(paidQuantity / buyQuantity) * rewardQuantity;

    widget.dataset.giftQuantity = String(giftQuantity);
    widget.querySelectorAll('[data-eun-cawa-gift-quantity-label]').forEach(label => {
      label.textContent = giftQuantity > 0
        ? `${widget.dataset.giftBadge || 'FREE'} × ${giftQuantity}`
        : 'Locked';
      label.classList.toggle('eun-cawa__free-label--locked', giftQuantity === 0);
    });

    const helper = widget.querySelector('[data-eun-cawa-gift-quantity-message]');
    if (helper) {
      const remaining = buyQuantity - (paidQuantity % buyQuantity || buyQuantity);
      helper.textContent = giftQuantity > 0
        ? `${giftQuantity} free gift${giftQuantity === 1 ? '' : 's'} will be added automatically.`
        : `Add ${remaining} more to unlock ${rewardQuantity} free gift${rewardQuantity === 1 ? '' : 's'}.`;
    }

    updateSelectedCount(widget);
  }

  function initWidget(widget) {
    if (widget.dataset.eunCawaReady === 'true') return;
    widget.dataset.eunCawaReady = 'true';

    const toggle = widget.querySelector('[data-eun-cawa-toggle]');
    const regularItems = [...widget.querySelectorAll('[data-eun-cawa-regular]')];
    const maxVisible = Number.parseInt(widget.dataset.maxVisible || '3', 10);
    const form = getFormForWidget(widget);

    widget.addEventListener('change', event => {
      if (event.target.matches('[data-eun-cawa-variant]')) updateSelectedCount(widget);
    });

    if (toggle) {
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        regularItems.slice(maxVisible).forEach(item => {
          item.hidden = expanded;
        });
        toggle.setAttribute('aria-expanded', String(!expanded));
        toggle.textContent = expanded
          ? `View ${Math.max(regularItems.length - maxVisible, 0)} more`
          : 'Show fewer';
      });
      toggle.setAttribute('aria-expanded', 'false');
    }

    if (isQuantityReward(widget)) {
      form?.addEventListener('input', event => {
        if (event.target.matches('[name="quantity"]')) updateQuantityRewardState(widget, form);
      });
      form?.addEventListener('change', event => {
        if (event.target.matches('[name="quantity"]')) updateQuantityRewardState(widget, form);
      });
      updateQuantityRewardState(widget, form);
    } else if (isGiftQuantity(widget)) {
      form?.addEventListener('input', event => {
        if (event.target.matches('[name="quantity"]')) updateGiftQuantityState(widget, form);
      });
      form?.addEventListener('change', event => {
        if (event.target.matches('[name="quantity"]')) updateGiftQuantityState(widget, form);
      });
      updateGiftQuantityState(widget, form);
    } else {
      updateSelectedCount(widget);
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll(widgetSelector).forEach(initWidget);
  }

  function getWidgetForForm(form) {
    const productId = form.dataset.productId;
    if (!productId) return null;
    return [...document.querySelectorAll(widgetSelector)].find(widget => widget.dataset.productId === productId) || null;
  }

  function setMessage(widget, message) {
    const output = widget.querySelector('[data-eun-cawa-message]');
    if (!output) return;
    output.textContent = message || '';
    output.hidden = !message;
  }

  function setLoading(form, loading) {
    const wrapper = form.closest('.product-form');
    const button = form.querySelector('[name="add"]');
    wrapper?.classList.toggle('adding', loading);
    if (button) {
      if (loading) {
        button.dataset.eunCawaWasDisabled = String(button.disabled);
        button.disabled = true;
      } else {
        button.disabled = button.dataset.eunCawaWasDisabled === 'true';
        delete button.dataset.eunCawaWasDisabled;
      }
      button.setAttribute('aria-busy', String(loading));
    }
  }

  function getMainItem(form) {
    const formData = new FormData(form);
    const id = Number.parseInt(String(formData.get('id') || ''), 10);
    const quantity = Math.max(1, Number.parseInt(String(formData.get('quantity') || '1'), 10) || 1);
    const properties = {};

    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)]$/);
      if (!match || value instanceof File || value === '') continue;
      properties[match[1]] = value;
    }

    const item = { id, quantity };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number.parseInt(String(sellingPlan), 10);
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  async function getCart() {
    const response = await fetch(`${getRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to read the cart. Please try again.');
    return response.json();
  }

  function isPromotionalGift(item) {
    return String(item.properties?._free_gift || '').toLowerCase() === 'true';
  }

  function getSelectedGiftVariantIds(widget) {
    return [...widget.querySelectorAll('[data-eun-cawa-gift-input]:checked')]
      .map(input => String(input.dataset.variantId || ''))
      .filter(Boolean);
  }

  function isGiftForProduct(item, qualifyingProductId) {
    if (!isPromotionalGift(item)) return false;
    const itemQualifyingProductId = String(item.properties?._qualifying_product_id || '');
    return !itemQualifyingProductId || itemQualifyingProductId === String(qualifyingProductId || '');
  }

  function isQuantityRewardQualifier(item, widget, variantId) {
    if (String(item.properties?._quantity_reward_qualifier || '').toLowerCase() !== 'true') return false;
    if (isPromotionalGift(item)) return false;
    if (String(item.properties?._quantity_reward_group || '') !== String(widget.dataset.productId || '')) return false;
    return String(item.variant_id) === String(variantId);
  }

  function isQuantityRewardGift(item, widget, variantId) {
    if (String(item.properties?._quantity_reward_gift || '').toLowerCase() !== 'true') return false;
    if (!isGiftForProduct(item, widget.dataset.productId)) return false;
    return String(item.variant_id) === String(variantId);
  }

  function isQuantityAccessoryGift(item, widget, qualifyingVariantId, accessoryVariantId) {
    if (String(item.properties?._quantity_reward_accessory_gift || '').toLowerCase() !== 'true') return false;
    if (!isGiftForProduct(item, widget.dataset.productId)) return false;
    if (String(item.properties?._quantity_reward_qualifying_variant_id || '') !== String(qualifyingVariantId)) return false;
    return String(item.variant_id) === String(accessoryVariantId);
  }

  function isGiftQuantityGift(item, widget) {
    return String(item.properties?._gift_quantity_reward || '').toLowerCase() === 'true'
      && isGiftForProduct(item, widget.dataset.productId);
  }

  function quantityRewardProperties(widget, variantId) {
    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    return {
      _free_gift: 'true',
      _quantity_reward_gift: 'true',
      _qualifying_product_id: String(widget.dataset.productId || ''),
      _qualifying_variant_id: String(variantId),
      _quantity_reward_group: String(widget.dataset.productId || ''),
      _quantity_reward_buy: String(buyQuantity),
      _quantity_reward_get: String(rewardQuantity)
    };
  }

  function quantityAccessoryProperties(widget, qualifyingVariantId, accessoryVariantId) {
    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    return {
      _free_gift: 'true',
      _quantity_reward_accessory_gift: 'true',
      _qualifying_product_id: String(widget.dataset.productId || ''),
      _quantity_reward_qualifying_variant_id: String(qualifyingVariantId),
      _quantity_reward_accessory_variant_id: String(accessoryVariantId),
      _quantity_reward_group: String(widget.dataset.productId || ''),
      _quantity_reward_buy: String(buyQuantity),
      _quantity_reward_get: String(rewardQuantity)
    };
  }

  function markMainItemAsQuantityQualifier(mainItem, widget) {
    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    const accessoryVariantId = getQuantityAccessoryVariantId(widget);
    mainItem.properties = {
      ...(mainItem.properties || {}),
      _quantity_reward_qualifier: 'true',
      _quantity_reward_group: String(widget.dataset.productId || ''),
      _quantity_reward_buy: String(buyQuantity),
      _quantity_reward_get: String(rewardQuantity)
    };
    if (accessoryVariantId) {
      mainItem.properties._quantity_reward_accessory_variant_id = String(accessoryVariantId);
    }
    return mainItem;
  }

  function giftQuantityProperties(widget, giftVariantId) {
    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    return {
      _free_gift: 'true',
      _gift_quantity_reward: 'true',
      _qualifying_product_id: String(widget.dataset.productId || ''),
      _gift_quantity_variant_id: String(giftVariantId),
      _gift_quantity_buy: String(buyQuantity),
      _gift_quantity_get: String(rewardQuantity)
    };
  }

  function markMainItemAsGiftQuantityQualifier(mainItem, widget, giftVariantId) {
    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    mainItem.properties = {
      ...(mainItem.properties || {}),
      _gift_quantity_qualifier: 'true',
      _gift_quantity_group: String(widget.dataset.productId || ''),
      _gift_quantity_variant_id: String(giftVariantId),
      _gift_quantity_buy: String(buyQuantity),
      _gift_quantity_get: String(rewardQuantity)
    };
    return mainItem;
  }

  async function prepareGiftQuantity(cart, mainItem, widget) {
    const selectedGiftVariantId = getSelectedGiftVariantIds(widget)[0];
    if (!selectedGiftVariantId) throw new Error('Please choose a free gift.');

    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    const paidInCart = (cart.items || [])
      .filter(item => !isPromotionalGift(item) && String(item.product_id) === String(widget.dataset.productId || ''))
      .reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    const desiredGiftQuantity = Math.floor((paidInCart + mainItem.quantity) / buyQuantity) * rewardQuantity;
    const giftLines = (cart.items || []).filter(item => isGiftForProduct(item, widget.dataset.productId));
    const reusableGiftLines = giftLines.filter(item =>
      isGiftQuantityGift(item, widget) && String(item.variant_id) === String(selectedGiftVariantId)
    );
    const updates = {};

    giftLines.forEach(item => {
      if (!item.key) return;
      const reusableIndex = reusableGiftLines.indexOf(item);
      const target = reusableIndex === 0 ? desiredGiftQuantity : 0;
      if (Number(item.quantity) !== target) updates[item.key] = target;
    });

    if (Object.keys(updates).length) {
      const settings = getSettings();
      const response = await fetch(settings.routes?.cart_update_url || `${getRoot()}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ updates })
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to update your free gifts.');
      }
      cart = result;
    }

    markMainItemAsGiftQuantityQualifier(mainItem, widget, selectedGiftVariantId);
    const giftItem = reusableGiftLines.length === 0 && desiredGiftQuantity > 0
      ? {
          id: Number.parseInt(selectedGiftVariantId, 10),
          quantity: desiredGiftQuantity,
          properties: giftQuantityProperties(widget, selectedGiftVariantId)
        }
      : null;

    return { cart, giftItem };
  }

  async function prepareQuantityReward(cart, mainItem, widget) {
    const { buyQuantity, rewardQuantity } = getQuantityRewardConfig(widget);
    const paidInCart = (cart.items || [])
      .filter(item => isQuantityRewardQualifier(item, widget, mainItem.id))
      .reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    const desiredGiftQuantity = Math.floor((paidInCart + mainItem.quantity) / buyQuantity) * rewardQuantity;
    const giftLines = (cart.items || []).filter(item => isQuantityRewardGift(item, widget, mainItem.id));
    const accessoryVariantId = getQuantityAccessoryVariantId(widget);
    const desiredAccessoryQuantity = accessoryVariantId ? paidInCart + mainItem.quantity : 0;
    const accessoryGiftLines = accessoryVariantId
      ? (cart.items || []).filter(item => isQuantityAccessoryGift(item, widget, mainItem.id, accessoryVariantId))
      : [];
    const updates = {};

    giftLines.forEach((item, index) => {
      const target = index === 0 ? desiredGiftQuantity : 0;
      if (item.key && Number(item.quantity) !== target) updates[item.key] = target;
    });

    accessoryGiftLines.forEach((item, index) => {
      const target = index === 0 ? desiredAccessoryQuantity : 0;
      if (item.key && Number(item.quantity) !== target) updates[item.key] = target;
    });

    if (Object.keys(updates).length) {
      const settings = getSettings();
      const response = await fetch(settings.routes?.cart_update_url || `${getRoot()}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ updates })
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to update your free item.');
      }
      cart = result;
    }

    const giftAlreadyExists = giftLines.length > 0;
    const giftItem = !giftAlreadyExists && desiredGiftQuantity > 0
      ? {
          id: mainItem.id,
          quantity: desiredGiftQuantity,
          properties: quantityRewardProperties(widget, mainItem.id)
        }
      : null;

    const accessoryAlreadyExists = accessoryGiftLines.length > 0;
    const accessoryGiftItem = !accessoryAlreadyExists && desiredAccessoryQuantity > 0
      ? {
          id: accessoryVariantId,
          quantity: desiredAccessoryQuantity,
          properties: quantityAccessoryProperties(widget, mainItem.id, accessoryVariantId)
        }
      : null;

    return { cart, giftItem, accessoryGiftItem };
  }

  async function syncExistingGiftChoices(cart, selectedGiftVariantIds, qualifyingProductId) {
    if (!selectedGiftVariantIds.length) return cart;

    const updates = {};
    const selectedIds = new Set(selectedGiftVariantIds.map(String));

    (cart.items || []).forEach(item => {
      if (!isGiftForProduct(item, qualifyingProductId) || !item.key) return;

      if (selectedIds.has(String(item.variant_id))) {
        if (Number(item.quantity) !== 1) updates[item.key] = 1;
        return;
      }

      updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return cart;

    const settings = getSettings();
    const response = await fetch(settings.routes?.cart_update_url || `${getRoot()}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });

    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to update your free gifts.');
    }
    return result;
  }

  function getSelectedItems(widget, cart) {
    const items = [];
    const seen = new Set();
    const qualifyingProductId = String(widget.dataset.productId || '');
    const promotionalGiftVariantIds = new Set(
      (cart.items || [])
        .filter(item => isGiftForProduct(item, qualifyingProductId))
        .map(item => String(item.variant_id))
    );

    widget.querySelectorAll('[data-eun-cawa-variant]:checked').forEach(input => {
      const id = String(input.dataset.variantId || '');
      if (!id || seen.has(id)) return;

      const isGift = input.hasAttribute('data-eun-cawa-gift-input');
      if (isGift && isGiftQuantity(widget)) return;
      if (isGift && promotionalGiftVariantIds.has(id)) return;

      const item = { id: Number.parseInt(id, 10), quantity: 1 };
      if (isGift) {
        item.properties = {
          _free_gift: 'true',
          _qualifying_product_id: widget.dataset.productId
        };
      }

      seen.add(id);
      items.push(item);
    });

    return items;
  }

  async function addItems(items, cartDrawer) {
    const settings = getSettings();
    const body = { items };

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer.getSectionsToRender().map(section => section.id);
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(settings.routes?.cart_add_url || `${getRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });

    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add these items to the cart.');
    }
    return result;
  }

  function showSuccess(result, cartDrawer) {
    const settings = getSettings();
    const strings = window.MinimogStrings || {};

    if (cartDrawer && settings.enable_cart_drawer && result.sections) {
      cartDrawer.renderContents(result);
      const drawerItems = cartDrawer.querySelector('m-cart-drawer-items');
      if (drawerItems && window.MinimogTheme?.Notification) {
        window.MinimogTheme.Notification.show({
          target: drawerItems,
          method: 'prepend',
          type: 'success',
          message: strings.itemAdded || 'Items added to cart',
          delay: 400
        });
      }
    } else {
      window.location.assign(settings.routes?.cart || `${getRoot()}cart`);
      return;
    }

    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, result);
    }
  }

  async function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(formSelector)) return;

    const widget = getWidgetForForm(form);
    if (!widget) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (widget.dataset.eunCawaSubmitting === 'true') return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setMessage(widget, '');
    setLoading(form, true);
    widget.dataset.eunCawaSubmitting = 'true';

    try {
      const mainItem = getMainItem(form);
      if (!Number.isFinite(mainItem.id)) throw new Error('Please select a product option before adding to cart.');

      let cart = await getCart();
      let quantityRewardGiftItem = null;
      let quantityAccessoryGiftItem = null;
      let giftQuantityItem = null;

      if (isQuantityReward(widget)) {
        markMainItemAsQuantityQualifier(mainItem, widget);
        const prepared = await prepareQuantityReward(cart, mainItem, widget);
        cart = prepared.cart;
        quantityRewardGiftItem = prepared.giftItem;
        quantityAccessoryGiftItem = prepared.accessoryGiftItem;
      } else if (isGiftQuantity(widget)) {
        const prepared = await prepareGiftQuantity(cart, mainItem, widget);
        cart = prepared.cart;
        giftQuantityItem = prepared.giftItem;
      } else {
        const selectedGiftVariantIds = getSelectedGiftVariantIds(widget);
        if (selectedGiftVariantIds.length) {
          cart = await syncExistingGiftChoices(cart, selectedGiftVariantIds, widget.dataset.productId);
        }
      }

      const selectedItems = getSelectedItems(widget, cart);
      const items = [mainItem];
      if (quantityRewardGiftItem) items.push(quantityRewardGiftItem);
      if (quantityAccessoryGiftItem) items.push(quantityAccessoryGiftItem);
      if (giftQuantityItem) items.push(giftQuantityItem);
      items.push(...selectedItems);
      const result = await addItems(items, document.querySelector('m-cart-drawer'));
      showSuccess(result, document.querySelector('m-cart-drawer'));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add these items to the cart.';
      setMessage(widget, message);
      const errorWrapper = form.querySelector('.prod__form-error') || form.closest('product-form')?.querySelector('.prod__form-error');
      if (errorWrapper && window.MinimogTheme?.Notification) {
        window.MinimogTheme.Notification.show({
          target: errorWrapper,
          method: 'appendChild',
          type: 'warning',
          message,
          last: 3000
        });
      }
    } finally {
      widget.dataset.eunCawaSubmitting = 'false';
      setLoading(form, false);
    }
  }

  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', event => initAll(event.target));

  if (document.readyState !== 'loading') initAll();
})();
